import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Slider,
  LinearProgress,
  IconButton,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SaveIcon from "@mui/icons-material/Save";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBannerPage from "../components/HeaderBannerPage";
import {
  fetchPmFrameList,
  insertPmDailyRemark,
  updatePmPhysicalCompletion,
  fetchPmDelayReasons,
} from "../actions/ProductionMonitorAction";

/* -------------------- Constants / helpers -------------------- */
const NAVY = "#142c54";
const BLUE = "#154c79";

// Display name for a component value: SHELL_GIRDER -> Shell Girder, DECK_01 -> Deck 01 (value sent to API stays the same)
const componentLabel = (v) =>
  String(v || "")
    .split("_")
    .map((w) => (w.length > 3 ? w.charAt(0) + w.slice(1).toLowerCase() : w))
    .join(" ");

// Local yyyy-MM-dd (toISOString shifts to UTC and can give the previous day in Sri Lanka)
const formatDate = (dateObj) => {
  const d = new Date(dateObj);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Always show / keep dates as yyyy-MM-dd
const toYmd = (v) => String(v || "").slice(0, 10);

// Auto-format typed digits as yyyy-MM-dd (e.g. 20261019 -> 2026-10-19)
const formatTypedDate = (text) => {
  const digits = String(text || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
};

const isValidYmd = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const [y, m, d] = v.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

const toNumber = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const cardSx = {
  p: { xs: 1.5, sm: 2.5 },
  borderRadius: "16px",
  border: "1px solid #d6dce5",
  backgroundColor: "#fff",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
};

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", backgroundColor: "#fff" } };

const InfoItem = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0E3C66", wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Box>
);

/* ---- CDL reason groups (common names) ---- */
// Codes not listed here show without a group
const CDL_REASON_GROUPS = {
  SF: "SWC Workshop Delay", // Flat bar blending
  SC: "SWC Workshop Delay", // Chamfer Cutting
  SB: "SWC Workshop Delay", // Bevel Cutting
  ST: "SWC Workshop Delay", // Transport Delay
  SM: "SWC Workshop Delay", // Material Delay
  SL: "SWC Workshop Delay", // Line heating delay
  IP: "Inspection Delay", // Production
  IQ: "Inspection Delay", // QC
  IO: "Inspection Delay", // ShipOwner
};
const cdlGroupOf = (code) => CDL_REASON_GROUPS[code] || "";

// Dropdown list: grouped reasons are hidden under their common name until it is clicked
const buildReasonOptions = (reasons, groupOf, expanded) => {
  const out = [];
  const added = {};
  (reasons || []).forEach((r) => {
    const g = groupOf ? groupOf(r.Code) : "";
    if (!g) {
      out.push(r);
      return;
    }
    if (added[g]) return;
    added[g] = true;
    const members = reasons.filter((x) => groupOf(x.Code) === g);
    out.push({ isGroup: true, Code: `__group_${g}`, Name: g, count: members.length });
    if (expanded[g]) members.forEach((m) => out.push({ ...m, inGroup: g }));
  });
  return out;
};

/* ---- Reason dropdown (short list, groups open on click) ---- */
const ReasonSelect = ({ title, reasons, reason, onReason, loading, groupOf }) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [inputValue, setInputValue] = useState("");
  const keepOpen = useRef(false);

  const selected = (reasons || []).find((r) => r.Code === reason) || null;
  const selectedGroup = selected && groupOf ? groupOf(selected.Code) : "";
  const searching = !!inputValue.trim() && (!selected || inputValue !== selected.Name);

  const expandedNow = expanded;
  const options = buildReasonOptions(reasons, groupOf, expandedNow);

  // typing searches every reason (names, codes and group names), no headers
  const filterOptions = (opts, { inputValue: text }) => {
    const q = text.trim().toLowerCase();
    if (!q || (selected && text === selected.Name)) return opts;
    return (reasons || [])
      .filter((o) => [o.Name, o.Code, groupOf ? groupOf(o.Code) : ""].some((v) => String(v || "").toLowerCase().includes(q)))
      .map((o) => (groupOf && groupOf(o.Code) ? { ...o, inGroup: groupOf(o.Code) } : o));
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        if (keepOpen.current) {
          keepOpen.current = false; // group header was clicked -> stay open
          return;
        }
        setOpen(false);
      }}
      options={options}
      loading={loading}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_, v, why) => {
        // clicking a group header must not put its name in the box
        if (why !== "input" && options.some((o) => o.isGroup && o.Name === v)) return;
        setInputValue(v);
      }}
      onChange={(_, v) => {
        if (v && v.isGroup) {
          keepOpen.current = true;
          setExpanded((prev) => ({ ...prev, [v.Name]: !expandedNow[v.Name] }));
          return;
        }
        // after choosing, only the chosen reason's group stays open
        const g = v && groupOf ? groupOf(v.Code) : "";
        setExpanded(g ? { [g]: true } : {});
        onReason(v ? v.Code : "");
      }}
      filterOptions={filterOptions}
      getOptionLabel={(o) => o.Name || ""}
      isOptionEqualToValue={(a, b) => a.Code === b.Code}
      renderOption={(props, o) =>
        o.isGroup ? (
          <li {...props} key={o.Code} style={{ ...props.style, backgroundColor: "#eef2f7" }}>
            <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1 }}>
              {expandedNow[o.Name] ? (
                <ExpandMoreIcon fontSize="small" sx={{ color: NAVY }} />
              ) : (
                <ChevronRightIcon fontSize="small" sx={{ color: NAVY }} />
              )}
              <Typography component="span" sx={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>
                {o.Name}
              </Typography>
              <Typography component="span" sx={{ ml: "auto", fontSize: "11px", color: "#6b7280" }}>
                {o.count}
              </Typography>
            </Box>
          </li>
        ) : (
          <li {...props} key={o.Code} style={{ ...props.style, paddingLeft: o.inGroup && !searching ? 44 : undefined }}>

            <Box sx={{ width: "100%", minWidth: 0, whiteSpace: "normal", wordBreak: "break-word" }}>
              <span>{o.Name}</span>
              {o.inGroup && searching && <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>{o.inGroup}</Typography>}
            </Box>
          </li>
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Reason"
          placeholder="Select reason"
          inputProps={{ ...params.inputProps, "aria-label": `${title} reason` }}
          helperText={selectedGroup || undefined}
          FormHelperTextProps={{ sx: { mx: 0.5, color: NAVY, fontWeight: 600 } }}
          sx={fieldSx}
        />
      )}
    />
  );
};

/* ---- One lost-time block (DTS or CDL): hours + reason + remark ---- */
const LostTimeBlock = ({ title, color, hours, onHours, hoursError, reasons, reason, onReason, reasonsLoading, remark, onRemark, groupOf }) => (
  <Box sx={{ p: 1.5, borderRadius: "12px", border: "1px solid #e3e8ef", backgroundColor: "#fafbfd" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
      <Box sx={{ width: 4, height: 16, borderRadius: 2, backgroundColor: color }} />
      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: NAVY }}>{title}</Typography>
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "110px minmax(0, 1fr)", sm: "130px minmax(0, 1fr)" }, gap: 1.5 }}>
      <TextField
        type="number"
        label="Lost Hours"
        placeholder="0"
        value={hours}
        onChange={(e) => onHours(e.target.value)}
        inputProps={{ min: 0, step: 0.5, inputMode: "decimal", "aria-label": `${title} lost hours` }}
        error={!!hoursError}
        helperText={hoursError}
        sx={fieldSx}
      />
      <ReasonSelect
        title={title}
        reasons={reasons}
        reason={reason}
        onReason={onReason}
        loading={reasonsLoading}
        groupOf={groupOf}
      />
      <TextField
        label="Remark"
        placeholder={`${title} delay remark`}
        value={remark}
        onChange={(e) => onRemark(e.target.value)}
        inputProps={{ "aria-label": `${title} remark` }}
        sx={{ ...fieldSx, gridColumn: "1 / -1" }}
        fullWidth
      />
    </Box>
  </Box>
);

/* -------------------- Page -------------------- */
const DailyRemark = () => {
  const dispatch = useDispatch();
  const {
    frameList,
    frameListLoading,
    frameListError,
    remarkLoading,
    completionLoading,
    cdlDelayReasons,
    dtsDelayReasons,
    delayReasonsLoading,
  } = useSelector(
    (state) => state.productionMonitor
  );

  const navigate = useNavigate();
  const [params] = useSearchParams(); // ?iwoNo=&frameId= when coming back from Remark History
  const [iwoNo, setIwoNo] = useState(() => params.get("iwoNo") || null); // step 1: IWO
  const [component, setComponent] = useState(null); // step 2: component of that IWO
  const [frameId, setFrameId] = useState(() => (params.get("frameId") ? Number(params.get("frameId")) : null)); // step 3: frame of that component
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const formRef = useRef(null); // phones: jump to the frame details + remark fields after a frame is chosen

  // Remark History is its own page (keeps this IWO / frame selected)
  const openHistory = () => {
    const q = new URLSearchParams();
    if (iwoNo) q.set("iwoNo", iwoNo);
    if (iwoNo && frameId) q.set("frameId", String(frameId));
    navigate(`/remarkHistory${q.toString() ? `?${q}` : ""}`);
  };
  const [workDate, setWorkDate] = useState(formatDate(new Date()));
  const datePickerRef = useRef(null);

  const openDatePicker = () => {
    const el = datePickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.click();
  };
  const [remark, setRemark] = useState("");
  const [lostHoursDts, setLostHoursDts] = useState("");
  const [lostHoursCdl, setLostHoursCdl] = useState("");
  const [dtsReason, setDtsReason] = useState("");
  const [dtsRemark, setDtsRemark] = useState("");
  const [cdlReason, setCdlReason] = useState("");
  const [cdlRemark, setCdlRemark] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const createdBy = localStorage.getItem("employeeId") || "";

  useEffect(() => {
    dispatch(fetchPmFrameList());
    dispatch(fetchPmDelayReasons());
  }, [dispatch]);

  // Sorted by IWO, then frame no — so options group nicely
  const frames = useMemo(
    () =>
      [...(frameList || [])].sort(
        (a, b) =>
          String(a.IwoNo).localeCompare(String(b.IwoNo), undefined, { numeric: true }) ||
          String(a.FrameNo).localeCompare(String(b.FrameNo), undefined, { numeric: true })
      ),
    [frameList]
  );

  // Step 1 list: one row per IWO (from GetFrameDetails), with Eng Spec and frame counts
  const iwoOptions = useMemo(() => {
    const map = new Map();
    frames.forEach((f) => {
      const key = String(f.IwoNo);
      if (!map.has(key)) map.set(key, { IwoNo: key, EngSpec: f.EngSpec || "", total: 0, completed: 0 });
      const row = map.get(key);
      row.total += 1;
      if (f.CompletionStatus === true) row.completed += 1;
      if (!row.EngSpec && f.EngSpec) row.EngSpec = f.EngSpec;
    });
    return Array.from(map.values());
  }, [frames]);
  const selectedIwo = useMemo(() => iwoOptions.find((o) => o.IwoNo === iwoNo) || null, [iwoOptions, iwoNo]);

  // frames of the chosen IWO
  const iwoFrames = useMemo(() => (iwoNo ? frames.filter((f) => String(f.IwoNo) === iwoNo) : []), [frames, iwoNo]);

  // Step 2 list: components of that IWO (with frame counts)
  const componentOptions = useMemo(() => {
    const map = new Map();
    iwoFrames.forEach((f) => {
      const key = f.ComponentName || "";
      if (!map.has(key)) map.set(key, { value: key, total: 0, completed: 0 });
      const row = map.get(key);
      row.total += 1;
      if (f.CompletionStatus === true) row.completed += 1;
    });
    return Array.from(map.values());
  }, [iwoFrames]);
  const componentOption = componentOptions.find((c) => c.value === component) || null;

  // Step 3 list: only the frames of that component (short list - easy on a phone)
  const componentFrames = useMemo(
    () => (component !== null ? iwoFrames.filter((f) => (f.ComponentName || "") === component) : []),
    [iwoFrames, component]
  );

  // coming back from Remark History with a frame -> also select that frame's component
  useEffect(() => {
    if (component === null && frameId) {
      const f = iwoFrames.find((x) => x.FrameId === frameId);
      if (f) setComponent(f.ComponentName || "");
    }
  }, [iwoFrames, frameId, component]);

  // choose a frame; on phones scroll down to the frame details (remark fields follow)
  const chooseFrame = (val) => {
    setFrameId(val ? val.FrameId : null);
    if (val && isMobile) {
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  };

  // Selected frame always comes from the latest list (history refreshes after save)
  const frame = useMemo(() => frames.find((f) => f.FrameId === frameId) || null, [frames, frameId]);

  /* ---- Physical completion ---- */
  const currentCompletion = Math.round(toNumber(frame?.PhysicalCompletion));
  const [completion, setCompletion] = useState(0);

  // reset the box to the saved value when the frame (or its saved value) changes
  useEffect(() => {
    setCompletion(currentCompletion);
  }, [frame?.FrameId, currentCompletion]);

  // completion can only go up: never lower than the saved value
  const completionValid =
    completion !== "" && !isNaN(completion) && completion >= currentCompletion && completion <= 100;
  const completionChanged = completionValid && Number(completion) !== currentCompletion;

  const handleUpdateCompletion = async () => {
    if (!frame || !completionChanged) return;
    const value = Math.round(Number(completion));
    const result = await dispatch(
      updatePmPhysicalCompletion({ frameId: String(frame.FrameId), physicalCompletion: value, updatedBy: createdBy })
    );
    if (result.success) {
      setSnack({ open: true, severity: "success", message: `${frame.FrameNo} completion updated to ${value}%` });
      dispatch(fetchPmFrameList()); // refresh saved value
    } else {
      setSnack({ open: true, severity: "error", message: result.message });
    }
  };

  const history = useMemo(
    () =>
      [...(frame?.DailyRemarks || [])].sort((a, b) =>
        String(b.WorkDate).localeCompare(String(a.WorkDate))
      ),
    [frame]
  );

  const sameDayExists = !!frame && history.some((r) => toYmd(r.WorkDate) === workDate);

  /* ---- Validation ---- */
  const errors = {
    frame: !frame ? "Select a frame" : "",
    workDate: !isValidYmd(workDate) ? "Enter work date as yyyy-mm-dd" : "",
    remark: !remark.trim() ? "Enter a remark" : "",
    lostHoursDts: toNumber(lostHoursDts) < 0 ? "Cannot be negative" : "",
    lostHoursCdl: toNumber(lostHoursCdl) < 0 ? "Cannot be negative" : "",
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const err = (k) => (showErrors ? errors[k] : "");

  /* ---- Save ---- */
  const handleSave = async () => {
    if (hasErrors) {
      setShowErrors(true);
      return;
    }

    const payload = {
      frameId: String(frame.FrameId),
      workDate,
      remark: remark.trim(),
      lostHoursDts: toNumber(lostHoursDts),
      lostHoursCdl: toNumber(lostHoursCdl),
      dtsReason, // code, e.g. "WC"
      dtsRemark: dtsRemark.trim(),
      cdlReason, // code, e.g. "SM"
      cdlRemark: cdlRemark.trim(),
      createdBy,
    };

    const result = await dispatch(insertPmDailyRemark(payload));

    if (result.success) {
      setSnack({ open: true, severity: "success", message: `Remark saved for ${frame.FrameNo} (${workDate})` });
      setRemark("");
      setLostHoursDts("");
      setLostHoursCdl("");
      setDtsReason("");
      setDtsRemark("");
      setCdlReason("");
      setCdlRemark("");
      setShowErrors(false);
      dispatch(fetchPmFrameList()); // refresh history
    } else {
      setSnack({ open: true, severity: "error", message: result.message });
    }
  };

  const partNos = (frame?.Parts || []).map((p) => p.PartNo);

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f5f7fb" }}>
      <HeaderBannerPage />

      <Box
        sx={{
          maxWidth: "1200px",
          width: "100%",
          mx: "auto",
          px: { xs: 1.5, sm: 2, md: 4 },
          py: { xs: 2, md: 3 },
          boxSizing: "border-box",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, maxWidth: 760, mx: "auto" }}>
          <Typography sx={{ fontSize: { xs: "19px", md: "22px" }, fontWeight: 700, color: "#0E3C66", letterSpacing: "0.5px" }}>
            Update Daily Remark
          </Typography>
          {/* Remark History page */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<HistoryIcon />}
            onClick={openHistory}
            aria-label="Remark history"
            sx={{
              ml: "auto",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              color: BLUE,
              borderColor: "#c9d6e6",
              backgroundColor: "#fff",
              minWidth: 0,
              px: { xs: 1, sm: 1.5 },
              "& .MuiButton-startIcon": { mr: { xs: 0, sm: 0.75 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              History
            </Box>
          </Button>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: 2,
            alignItems: "start",
            maxWidth: 760,
            mx: "auto",
          }}
        >
          {/* ── Left: frame + form ── */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* IWO -> Component -> Frame */}
            <Paper elevation={0} sx={cardSx}>
              {/* Step 1: IWO + Eng Spec */}
              <Autocomplete
                options={iwoOptions}
                value={selectedIwo}
                onChange={(_, val) => {
                  setIwoNo(val ? val.IwoNo : null);
                  setComponent(null); // pick component + frame again for the new IWO
                  setFrameId(null);
                }}
                loading={frameListLoading}
                autoHighlight
                getOptionLabel={(o) => (o ? `${o.IwoNo}${o.EngSpec ? ` - ${o.EngSpec}` : ""}` : "")}
                isOptionEqualToValue={(o, v) => o.IwoNo === v.IwoNo}
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter((o) => [o.IwoNo, o.EngSpec].some((v) => String(v || "").toLowerCase().includes(q)));
                }}
                renderOption={(props, o) => (
                  <Box component="li" {...props} key={o.IwoNo} sx={{ display: "block !important", py: "8px !important" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0E3C66" }}>{o.IwoNo}</Typography>
                      <Typography sx={{ ml: "auto", fontSize: "11px", color: "#6b7280", whiteSpace: "nowrap" }}>
                        {o.total} frame(s){o.completed > 0 ? ` · ${o.completed} done` : ""}
                      </Typography>
                    </Box>
                    {o.EngSpec && (
                      <Typography sx={{ fontSize: "12px", color: "#555", whiteSpace: "normal", wordBreak: "break-word" }}>{o.EngSpec}</Typography>
                    )}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="IWO No - Eng Spec"
                    placeholder="Search IWO no or eng spec"
                    error={!!frameListError || (!!err("frame") && !iwoNo)}
                    // full Eng Spec under the box (the box itself cuts long text on phones)
                    helperText={frameListError || (!iwoNo ? err("frame") && "Select an IWO" : selectedIwo?.EngSpec || "")}
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {frameListLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {/* Step 2: component of that IWO */}
              <Autocomplete
                sx={{ mt: 2 }}
                options={componentOptions}
                value={componentOption}
                onChange={(_, val) => {
                  setComponent(val ? val.value : null);
                  setFrameId(null); // pick the frame again for the new component
                }}
                disabled={!iwoNo}
                autoHighlight
                getOptionLabel={(c) => (c ? componentLabel(c.value) || "-" : "")}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                noOptionsText="No components for this IWO"
                renderOption={(props, c) => (
                  <Box component="li" {...props} key={c.value} sx={{ display: "flex !important", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0E3C66" }}>{componentLabel(c.value) || "-"}</Typography>
                    <Typography sx={{ ml: "auto", fontSize: "11px", color: "#6b7280", whiteSpace: "nowrap" }}>
                      {c.total} frame(s){c.completed > 0 ? ` · ${c.completed} done` : ""}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Component"
                    placeholder={iwoNo ? "Select component" : "Select an IWO first"}
                    error={!!iwoNo && component === null && !!err("frame")}
                    helperText={iwoNo && component === null && err("frame") ? "Select a component" : ""}
                    sx={fieldSx}
                  />
                )}
              />

              {/* Step 3: frames of that component */}
              <Autocomplete
                sx={{ mt: 2 }}
                options={componentFrames}
                value={frame}
                onChange={(_, val) => chooseFrame(val)}
                disabled={!iwoNo || component === null}
                autoHighlight
                getOptionLabel={(f) =>
                  f ? `${f.FrameNo} · ${componentLabel(f.ComponentName)}` : ""
                }
                isOptionEqualToValue={(o, v) => o.FrameId === v.FrameId}
                noOptionsText="No frames for this component"
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter((f) =>
                    [f.FrameNo, f.ComponentName, componentLabel(f.ComponentName), ...(f.Parts || []).map((p) => p.PartNo)]
                      .some((v) => String(v || "").toLowerCase().includes(q))
                  );
                }}
                renderOption={(props, f) => (
                  <Box component="li" {...props} key={f.FrameId} sx={{ display: "block !important", py: "8px !important" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0E3C66" }}>{f.FrameNo}</Typography>
                      <Chip label={componentLabel(f.ComponentName)} size="small" sx={{ height: 20, fontSize: "11px" }} />
                      {f.CompletionStatus && <Chip label="Completed" size="small" color="success" sx={{ height: 20, fontSize: "11px" }} />}
                    </Box>
                    <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                      {toYmd(f.StartDate)} → {toYmd(f.EndDate)} · {(f.Parts || []).length} parts
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Frame"
                    placeholder={!iwoNo ? "Select an IWO first" : component === null ? "Select a component first" : "Search frame or part no"}
                    error={component !== null && !!err("frame")}
                    helperText={component !== null ? err("frame") || `${componentFrames.length} frame(s) in ${componentLabel(component) || "-"}` : ""}
                    sx={fieldSx}
                  />
                )}
              />
            </Paper>

            {/* Frame details + physical completion (before the remark fields) */}
            {frame && (
              <Paper elevation={0} sx={{ ...cardSx, scrollMarginTop: "72px" }} ref={formRef}>
                <Box sx={{ p: 1.5, borderRadius: "12px", backgroundColor: "#f3f6fa" }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                      gap: 1.5,
                    }}
                  >
                    <InfoItem label="IWO" value={frame.IwoNo} />
                    <InfoItem label="Component" value={componentLabel(frame.ComponentName)} />
                    <InfoItem label="Start" value={toYmd(frame.StartDate)} />
                    <InfoItem label="End" value={toYmd(frame.EndDate)} />
                  </Box>
                  {partNos.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.5 }}>
                        Parts ({partNos.length})
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {partNos.map((p) => (
                          <Chip key={p} label={p} size="small" sx={{ backgroundColor: "#e8f0fa", color: "#0E3C66", fontWeight: 600 }} />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Physical completion */}
                  <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px dashed #d6dce5" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        Physical Completion
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                        Saved: <b style={{ color: NAVY }}>{currentCompletion}%</b>
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.max(0, currentCompletion))}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        mb: 1,
                        backgroundColor: "#e1e7ef",
                        "& .MuiLinearProgress-bar": { backgroundColor: currentCompletion >= 100 ? "#2e7d32" : BLUE },
                      }}
                    />
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr auto", sm: "1fr 96px auto" },
                        gap: 1.5,
                        alignItems: "center",
                      }}
                    >
                      <Slider
                        value={completionValid ? Number(completion) : 0}
                        onChange={(_, v) => setCompletion(Math.max(currentCompletion, v))}
                        step={5}
                        min={0}
                        max={100}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}%`}
                        disabled={completionLoading || currentCompletion >= 100}
                        sx={{ gridColumn: { xs: "1 / -1", sm: "auto" }, color: BLUE, mx: 1, width: "auto" }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        value={completion}
                        onChange={(e) => setCompletion(e.target.value === "" ? "" : Number(e.target.value))}
                        onBlur={() => {
                          // typed a lower value -> bring it back to the saved value
                          if (completion === "" || isNaN(completion) || completion < currentCompletion) setCompletion(currentCompletion);
                          else if (completion > 100) setCompletion(100);
                        }}
                        inputProps={{ min: currentCompletion, max: 100, step: 1, inputMode: "numeric", "aria-label": "Physical completion %" }}
                        InputProps={{ endAdornment: <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>%</Typography> }}
                        error={!completionValid}
                        sx={{ ...fieldSx, "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff" } }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleUpdateCompletion}
                        disabled={!completionChanged || completionLoading}
                        startIcon={completionLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                          height: 40,
                          borderRadius: "10px",
                          textTransform: "none",
                          fontWeight: 700,
                          backgroundColor: NAVY,
                          "&:hover": { backgroundColor: "#0D47A1" },
                        }}
                      >
                        Update
                      </Button>
                    </Box>
                    {!completionValid && (
                      <Typography sx={{ fontSize: "11px", color: "#c62828", mt: 0.5 }}>
                        {Number(completion) < currentCompletion
                          ? `Can't be lower than the saved value (${currentCompletion}%)`
                          : `Enter a value from ${currentCompletion} to 100`}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Form */}
            <Paper elevation={0} sx={cardSx}>
              <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 2 }}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    label="Work Date"
                    placeholder="yyyy-mm-dd"
                    value={workDate}
                    onChange={(e) => setWorkDate(formatTypedDate(e.target.value))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ inputMode: "numeric", maxLength: 10 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton edge="end" size="small" onClick={openDatePicker} aria-label="Pick work date">
                            <CalendarMonthIcon sx={{ color: BLUE }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={!!err("workDate")}
                    helperText={err("workDate")}
                    sx={fieldSx}
                    fullWidth
                  />
                  {/* Hidden native picker - only used to open the calendar; value stays yyyy-mm-dd */}
                  <input
                    ref={datePickerRef}
                    type="date"
                    tabIndex={-1}
                    aria-hidden="true"
                    value={isValidYmd(workDate) ? workDate : ""}
                    onChange={(e) => e.target.value && setWorkDate(e.target.value)}
                    style={{ position: "absolute", right: 12, bottom: 0, width: 1, height: 1, opacity: 0, border: 0, padding: 0, pointerEvents: "none" }}
                  />
                </Box>

                {sameDayExists && (
                  <Alert severity="warning" sx={{ borderRadius: "12px", py: 0 }}>
                    A remark already exists for this frame on {workDate}.
                  </Alert>
                )}

                <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 1.5 }}>
                  <LostTimeBlock
                    title="DTS"
                    color={BLUE}
                    hours={lostHoursDts}
                    onHours={setLostHoursDts}
                    hoursError={err("lostHoursDts")}
                    reasons={dtsDelayReasons || []}
                    reason={dtsReason}
                    onReason={setDtsReason}
                    reasonsLoading={delayReasonsLoading}
                    remark={dtsRemark}
                    onRemark={setDtsRemark}
                  />
                  <LostTimeBlock
                    title="CDL"
                    color="#e0a100"
                    hours={lostHoursCdl}
                    onHours={setLostHoursCdl}
                    hoursError={err("lostHoursCdl")}
                    reasons={cdlDelayReasons || []}
                    groupOf={cdlGroupOf}
                    reason={cdlReason}
                    onReason={setCdlReason}
                    reasonsLoading={delayReasonsLoading}
                    remark={cdlRemark}
                    onRemark={setCdlRemark}
                  />
                </Box>

                <TextField
                  label="Remark"
                  placeholder="What happened today on this frame?"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  multiline
                  rows={3}
                  error={!!err("remark")}
                  helperText={err("remark")}
                  sx={fieldSx}
                  fullWidth
                />

                <Box
                  sx={{
                    // phones: keep Save reachable at the bottom while typing
                    position: { xs: "sticky", md: "static" },
                    bottom: 0,
                    zIndex: 5,
                    mx: { xs: -1.5, md: 0 },
                    mb: { xs: -1.5, md: 0 },
                    p: { xs: 1.5, md: 0 },
                    backgroundColor: { xs: "rgba(255,255,255,0.97)", md: "transparent" },
                    borderBottomLeftRadius: "16px",
                    borderBottomRightRadius: "16px",
                  }}
                >
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={remarkLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  disabled={remarkLoading}
                  onClick={handleSave}
                  sx={{
                    height: 50,
                    borderRadius: "12px",
                    backgroundColor: "#2e7d32",
                    fontWeight: 700,
                    fontSize: "15px",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#1b5e20" },
                    "&:disabled": { backgroundColor: "#cccccc", color: "#666666" },
                  }}
                >
                  Save Remark
                </Button>
                </Box>
              </Box>
            </Paper>
          </Box>

        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DailyRemark;